import React from "react";
import { useLang } from "@/i18n/LanguageContext";
import { EQUIPMENT_PACKAGES, formatIDR } from "@/data/equipmentPackages";
import { Check, Package, Sparkles } from "lucide-react";

const ACCENT = {
  amber:   { badge: "bg-amber-600 text-white",   ring: "ring-amber-500/40",  dot: "text-amber-600" },
  slate:   { badge: "bg-slate-800 text-white",   ring: "ring-slate-500/40",  dot: "text-slate-700" },
  emerald: { badge: "bg-emerald-700 text-white", ring: "ring-emerald-500/40",dot: "text-emerald-700" },
  sky:     { badge: "bg-sky-700 text-white",     ring: "ring-sky-500/40",    dot: "text-sky-700" },
  rose:    { badge: "bg-rose-700 text-white",    ring: "ring-rose-500/40",   dot: "text-rose-700" },
};

/**
 * EquipmentPackagesSection
 * Displays 5 Umrah equipment packages as informational cards.
 * Purely presentational — booking selection happens in <BookingDialog />.
 */
export default function EquipmentPackagesSection() {
  const { lang } = useLang();
  return (
    <section
      data-testid="equipment-packages-section"
      className="mt-20 border-t border-border pt-12"
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
        <div>
          <p className="overline">{lang === "id" ? "Add-On Perlengkapan" : "Equipment Add-Ons"}</p>
          <h3 className="font-serif text-3xl lg:text-4xl text-primary mt-2 flex items-center gap-3">
            <Package className="h-7 w-7 text-accent" />
            {lang === "id" ? "Paket Perlengkapan Umrah" : "Umrah Equipment Packages"}
          </h3>
          <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
            {lang === "id"
              ? "Pilih paket perlengkapan yang bisa dipilih sesuai kebutuhan selama perjalanan di Tanah Suci. Perlengkapan bersifat add-on terpisah, dipilih saat booking."
              : "Choose an equipment package tailored to your Holy Land journey. Equipment is a separate add-on selectable at booking."}
          </p>
        </div>
        <div className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] bg-accent/60 text-primary px-3 py-1.5 rounded-full">
          <Sparkles className="h-3 w-3" />
          {lang === "id" ? "Opsional — Add-on" : "Optional Add-on"}
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        {EQUIPMENT_PACKAGES.map((eq) => {
          const a = ACCENT[eq.accent] || ACCENT.slate;
          const items = lang === "id" ? eq.items_id : eq.items_en;
          const name = lang === "id" ? eq.name_id : eq.name_en;
          const note = lang === "id" ? eq.note_id : eq.note_en;
          return (
            <div
              key={eq.id}
              data-testid={`equipment-card-${eq.id}`}
              className={`relative rounded-xl border border-border bg-card p-6 flex flex-col hover:shadow-lg transition-shadow ring-1 ${a.ring}`}
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${a.badge}`}>
                  {eq.tier}
                </span>
                <div className="text-right">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {lang === "id" ? "Harga" : "Price"}
                  </p>
                  <p className="font-serif text-xl text-primary font-semibold leading-tight">
                    {formatIDR(eq.price_idr)}
                  </p>
                </div>
              </div>
              <h4 className="font-serif text-lg text-primary leading-snug">{name}</h4>
              {note && <p className="text-xs text-muted-foreground mt-1">{note}</p>}

              {eq.upgrade_price_idr && (
                <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
                  <p className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold">
                    {lang === "id" ? "Upgrade" : "Upgrade"} · {formatIDR(eq.upgrade_price_idr)}
                  </p>
                  <p className="text-xs text-amber-900 mt-0.5">
                    {lang === "id" ? eq.upgrade_note_id : eq.upgrade_note_en}
                  </p>
                </div>
              )}

              <ul className="mt-4 space-y-1.5 flex-1">
                {items.map((it, i) => (
                  <li key={i} className="flex gap-2 text-xs text-foreground/80 leading-snug">
                    <Check className={`h-3.5 w-3.5 shrink-0 mt-0.5 ${a.dot}`} />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>

      <p className="mt-6 text-xs text-muted-foreground italic">
        {lang === "id"
          ? "* Harga perlengkapan belum termasuk dalam harga paket umrah. Pilih perlengkapan pada form pemesanan."
          : "* Equipment price is not included in the umrah package. Select equipment on the booking form."}
      </p>
    </section>
  );
}
